import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { IconAlertTriangle, IconArrowsExchange, IconCircleCheck, IconCircleX } from "@tabler/icons-react"
import { skipToken } from "@reduxjs/toolkit/query/react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { canManageAppointments } from "@/lib/appointments-access"
import { useAppSelector } from "@/store"
import {
  useGetCalUsersQuery,
  useGetTeamMembersQuery,
  useGetTeamsQuery,
} from "@/app/booking-calendar/bookingCalendarApi"
import { AffectedBookingsList } from "@/app/booking-calendar/components/AffectedBookingsList"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import "@/app/booking-calendar/bookingCalendar.css"
import type { StaffMoveRequest, StaffMoveResult, StaffMoveSiteResult } from "./staffMovesApi"
import { useMoveStaffMutation, usePreviewStaffMoveQuery } from "./staffMovesApi"
import {
  EMPTY_FORM,
  availabilityLink,
  buildMoveRequest,
  describeMoveError,
  effectiveLabel,
  formProblems,
  sameMove,
  siteFailed,
  siteJobStatus,
  siteStatusLabel,
} from "./lib/staffMoveModel"
import type { MoveForm } from "./lib/staffMoveModel"

/**
 * Appointments → Move Staff (AB#714): take a host off one site from an effective date-time and put
 * them on another. Preview lists their appointments at the old site from that time on, each with
 * Move to another host and Cancel; Confirm ends their availability at the old site (now, or at the
 * effective time) and adds them to the new one, unavailable until their hours are set there.
 * appointments:manage throughout, which is what both endpoints take.
 */
export default function StaffMovesPage() {
  const permissions = useAppSelector((state) => state.permissions.permissions)
  const canManage = canManageAppointments(permissions)
  const timeZone = SITE_TIME_ZONE

  const [form, setForm] = useState<MoveForm>(EMPTY_FORM)
  const [previewed, setPreviewed] = useState<StaffMoveRequest | null>(null)
  const [result, setResult] = useState<StaffMoveResult | null>(null)
  const [moveErrors, setMoveErrors] = useState<string[]>([])

  const { data: teams = [], isLoading: teamsLoading } = useGetTeamsQuery()
  const sites = useMemo(
    () => teams.filter(team => !!team.slug).sort((a, b) => a.name.localeCompare(b.name)),
    [teams],
  )
  const fromSite = sites.find(team => team.slug === form.fromTeamSlug)
  const siteName = (slug: string) => sites.find(team => team.slug === slug)?.name ?? slug

  // The old site's hosts, named as the availability page names them.
  const { data: members = [] } = useGetTeamMembersQuery(fromSite ? { teamId: fromSite.id } : skipToken)
  const { data: calUsers = [] } = useGetCalUsersQuery(fromSite ? undefined : skipToken)
  const hosts = useMemo(() => {
    const byId = new Map(calUsers.map(user => [user.id, user]))
    return members
      .filter(member => member.accepted)
      .map(member => {
        const user = byId.get(member.userId)
        return { id: member.userId, name: user?.name || user?.email || `Cal user ${member.userId}`, email: user?.email ?? null }
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [members, calUsers])

  const request = buildMoveRequest(form, timeZone)
  const problems = formProblems(form, timeZone)
  const previewQuery = usePreviewStaffMoveQuery(previewed ?? skipToken)
  const preview = previewed ? previewQuery.data : undefined
  const previewCurrent = sameMove(request, previewed) && !!preview
  const [moveStaff, moveState] = useMoveStaffMutation()

  function update(next: Partial<MoveForm>) {
    setForm(current => ({ ...current, ...next }))
    setResult(null)
    setMoveErrors([])
  }

  function runPreview() {
    if (!request) return
    setResult(null)
    setMoveErrors([])
    if (sameMove(request, previewed)) {
      void previewQuery.refetch()
    } else {
      setPreviewed(request)
    }
  }

  async function confirm() {
    if (!previewed || !previewCurrent) return
    setMoveErrors([])
    try {
      setResult(await moveStaff(previewed).unwrap())
    } catch (err) {
      setMoveErrors(describeMoveError(err))
    }
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center gap-2">
        <IconArrowsExchange className="size-6" />
        <h1 className="text-2xl font-bold">Move Staff</h1>
      </div>

      <p className="text-muted-foreground max-w-3xl">
        Move a host from one site to another from a date and time. Their availability at the old site
        ends then, and they join the new site unavailable until you set their hours there. Their
        appointments at the old site from that time on are listed first, so you can move or cancel them.
      </p>

      {!canManage ? (
        <p className="text-sm text-muted-foreground">Moving staff between sites needs appointments:manage.</p>
      ) : (
        <>
          <Card>
            <CardContent className="flex flex-wrap items-end gap-6">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="move-from">Moving from</Label>
                <Select
                  value={form.fromTeamSlug}
                  onValueChange={(slug) => update({ fromTeamSlug: slug, calUserId: null, upn: null })}
                >
                  <SelectTrigger id="move-from" className="w-60">
                    <SelectValue placeholder={teamsLoading ? "Loading sites…" : "Choose a site"} />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(team => (
                      <SelectItem key={team.id} value={team.slug!}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="move-host">Host</Label>
                <Select
                  value={form.calUserId ? String(form.calUserId) : ""}
                  onValueChange={(id) => {
                    const host = hosts.find(h => h.id === Number(id))
                    update({ calUserId: Number(id), upn: host?.email ?? null })
                  }}
                  disabled={!fromSite}
                >
                  <SelectTrigger id="move-host" className="w-60">
                    <SelectValue placeholder={fromSite ? "Choose a host" : "Choose a site first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {hosts.map(host => (
                      <SelectItem key={host.id} value={String(host.id)}>{host.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="move-to">Moving to</Label>
                <Select value={form.toTeamSlug} onValueChange={(slug) => update({ toTeamSlug: slug })}>
                  <SelectTrigger id="move-to" className="w-60">
                    <SelectValue placeholder={teamsLoading ? "Loading sites…" : "Choose a site"} />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.filter(team => team.slug !== form.fromTeamSlug).map(team => (
                      <SelectItem key={team.id} value={team.slug!}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="move-effective">Effective date-time (Pacific)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="move-effective"
                    type="datetime-local"
                    className="w-56"
                    value={form.effectiveAt}
                    onChange={(e) => update({ effectiveAt: e.target.value })}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={form.effectiveAt === ""}
                    onClick={() => update({ effectiveAt: "" })}
                  >
                    Now
                  </Button>
                </div>
                <span className="text-xs text-muted-foreground">
                  {form.effectiveAt === "" ? "Blank: the move takes effect now." : "The old site stops offering them at this time."}
                </span>
              </div>

              <Button disabled={!request || previewQuery.isFetching} onClick={runPreview}>
                {previewQuery.isFetching ? "Previewing…" : "Preview"}
              </Button>
            </CardContent>
          </Card>

          {problems.length > 0 && (form.fromTeamSlug || form.toTeamSlug || form.effectiveAt) && (
            <ul className="text-sm text-muted-foreground list-disc pl-5">
              {problems.map(problem => <li key={problem}>{problem}</li>)}
            </ul>
          )}

          {previewed && previewQuery.isError && (
            <Alert variant="destructive">
              <IconCircleX />
              <AlertTitle>The move could not be previewed</AlertTitle>
              <AlertDescription>
                {describeMoveError(previewQuery.error).map(message => <p key={message}>{message}</p>)}
              </AlertDescription>
            </Alert>
          )}

          {preview && !result && (
            <section className="flex flex-col gap-4">
              <h2 className="text-lg font-semibold">
                {`Moving from ${siteName(preview.fromTeamSlug)} to ${siteName(preview.toTeamSlug)}, effective ${effectiveLabel(preview.effectiveAt, preview.immediate, timeZone)}`}
              </h2>

              {!previewCurrent && (
                <p className="text-sm text-muted-foreground">The form has changed since this preview. Preview again before confirming.</p>
              )}

              {preview.warnings.map(warning => (
                <Alert key={warning.code}>
                  <IconAlertTriangle />
                  <AlertTitle>{warningTitle(warning.code)}</AlertTitle>
                  <AlertDescription>{warning.message}</AlertDescription>
                </Alert>
              ))}

              <div className="flex flex-col gap-2">
                <h3 className="font-medium">
                  {`Their appointments at ${siteName(preview.fromTeamSlug)} from then on (${preview.bookings.length})`}
                </h3>
                <AffectedBookingsList
                  bookings={preview.bookings}
                  timeZone={timeZone}
                  canManage={canManage}
                  emptyMessage="They have no appointments at the old site from the effective time on."
                />
                {preview.bookings.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Appointments left here keep their host after the move; move or cancel them first, or
                    find them later under Unassigned Hosts.
                  </p>
                )}
              </div>

              {moveErrors.length > 0 && (
                <Alert variant="destructive">
                  <IconCircleX />
                  <AlertTitle>The move was not made</AlertTitle>
                  <AlertDescription>{moveErrors.map(message => <p key={message}>{message}</p>)}</AlertDescription>
                </Alert>
              )}

              <div>
                <Button disabled={!previewCurrent || moveState.isLoading} onClick={() => void confirm()}>
                  {moveState.isLoading ? "Moving…" : "Confirm move"}
                </Button>
              </div>
            </section>
          )}

          {result && <MoveResults result={result} siteName={siteName} timeZone={timeZone} />}
        </>
      )}
    </div>
  )
}

function warningTitle(code: string): string {
  switch (code) {
    case "outreach-overrides":
      return "Outreach days already scheduled stay open"
    case "part-day-overrides":
      return "Part-day overrides will block the change"
    case "future-plan":
      return "A planned schedule will never apply"
    case "already-at-to-site":
      return "Already at the new site"
    default:
      return "Check before moving"
  }
}

/** What the move did, one card per site, with the way to set their hours at the new one. */
export function MoveResults({ result, siteName, timeZone }: {
  result: StaffMoveResult
  siteName: (slug: string) => string
  timeZone: string
}) {
  const toSite = result.sites.find(site => site.role === "to")
  const fromSite = result.sites.find(site => site.role === "from")

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">
        {`Moved from ${siteName(result.fromTeamSlug)} to ${siteName(result.toTeamSlug)}, effective ${effectiveLabel(result.effectiveAt, result.immediate, timeZone)}`}
      </h2>
      <div className="grid gap-4 md:grid-cols-2">
        {toSite && <SiteResultCard site={toSite} name={siteName(toSite.teamSlug)} />}
        {fromSite && <SiteResultCard site={fromSite} name={siteName(fromSite.teamSlug)} />}
      </div>
      {toSite && toSite.status !== "failed" && (
        <div>
          <Button asChild>
            <Link to={availabilityLink(toSite.teamSlug, toSite.calUserId)}>
              {`Set hours at ${siteName(toSite.teamSlug)}`}
            </Link>
          </Button>
        </div>
      )}
    </section>
  )
}

function SiteResultCard({ site, name }: { site: StaffMoveSiteResult; name: string }) {
  const failed = siteFailed(site)
  const job = siteJobStatus(site)
  const schedules = site.schedules ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {failed ? <IconCircleX className="size-5 text-destructive" /> : <IconCircleCheck className="size-5 text-green-700" />}
          {name}
          <Badge variant="outline">{site.role === "to" ? "New site" : "Old site"}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        <p>{siteStatusLabel(site)}</p>
        {site.error && <p role="alert" className="text-destructive">{site.error}</p>}
        {job && <p className="text-muted-foreground">{`Schedule update: ${job}`}</p>}
        {schedules.length > 0 && (
          <ul className="list-disc pl-5 text-muted-foreground">
            {schedules.map(schedule => (
              <li key={`${schedule.target}:${schedule.detail ?? ""}`}>
                {`${schedule.target}: ${schedule.detail ?? (schedule.succeeded ? "done" : "failed")}`}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
